/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";

const getBaseUrl = () => {
  if (typeof window !== "undefined") {
    return "/api/proxy";
  }
  return process.env.NEXT_PUBLIC_API_BASE_URL ;
};

interface QueueItem {
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}

class ApiClient {
  private axiosInstance: AxiosInstance;
  private isRefreshing: boolean = false;
  private failedQueue: QueueItem[] = [];

  constructor() {
    if (typeof window === "undefined") {
      // SSR safety — axiosInstance must never be used server-side
      this.axiosInstance = axios.create({ baseURL: "/api/proxy" });
      return;
    }

    this.axiosInstance = axios.create({
      baseURL: getBaseUrl(),
      timeout: 25000,
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.axiosInstance.interceptors.response.use(
      (response) => {
        console.log(`[Response] ${response.status} ${response.config.url}`);
        return response;
      },
      async (error: AxiosError) => {
        return this.handleResponseError(error);
      },
    );
  }

  private async handleResponseError(error: AxiosError): Promise<any> {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (!error.config) {
      return Promise.reject(error);
    }

    console.error("[Response Error]", error);

    // 502 cloudflare error interceptor
    if (error.response?.status === 502) {
      const cfRay = error.response.headers["cf-ray"];

      if (cfRay) {
        console.error(`[Cloudflare 502] Found Cloudflare Ray ID: ${cfRay}`);
      } else {
        console.error("[Cloudflare 502] Hit a 502 error, but no cf-ray header was found.");
      }
    }

    // Check if it's a 401
    if (error.response?.status === 401) {
      console.error("[Axios 401]", {
        url: originalRequest?.url,
        baseURL: originalRequest?.baseURL,
        method: originalRequest?.method,
        hasAuthHeader: !!originalRequest?.headers?.Authorization,
        cfRay: error.response.headers["cf-ray"] ?? "MISSING",
        cfCacheStatus: error.response.headers["cf-cache-status"] ?? "MISSING",
        isSSR: typeof window === "undefined",
        timestamp: new Date().toISOString(),
      });
      const errorData = error.response.data as any;
      
      // Check if it's a token expiration that the proxy couldn't handle
      if (errorData?.code === "TOKEN_EXPIRED" || error.response.headers['x-auth-failed']) {
        console.log("[Auth] Token expired and refresh failed - redirecting to login");
        this.redirectToLogin();
        return Promise.reject(error);
      }

      // If we haven't retried yet, try once more (proxy might have refreshed)
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        
        if (this.isRefreshing) {
          console.log("[Auth] Request queued while refresh in progress");
          return this.addToQueue(originalRequest);
        }

        this.isRefreshing = true;

        try {
          console.log("[Auth] Retrying request after potential token refresh...");

          await new Promise(resolve => setTimeout(resolve, 100));
          
          const result = await this.axiosInstance(originalRequest);
          this.processQueue(null);
          return result;
        } catch (retryError) {
          console.error("[Auth] Retry failed:", retryError);
          this.processQueue(retryError as Error);
          this.redirectToLogin();
          return Promise.reject(retryError);
        } finally {
          this.isRefreshing = false;
        }
      }
      console.log("[Auth] Already retried - redirecting to login");
      this.redirectToLogin();
    }

    return Promise.reject(error);
  }

  private async addToQueue(
    originalRequest: InternalAxiosRequestConfig,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.failedQueue.push({ resolve, reject });
    })
      .then(() => {
        console.log("[Auth] Retrying queued request");
        return this.axiosInstance(originalRequest);
      })
      .catch((err) => {
        return Promise.reject(err);
      });
  }

  private processQueue(error: Error | null): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    this.failedQueue = [];
  }

  private redirectToLogin(): void {
    console.log("[Auth] Redirecting to sign-in...");
    this.failedQueue = [];
    this.isRefreshing = false;
    
    // Safety check for Server-Side Rendering
    if (typeof window !== "undefined") {
      window.location.href = "/sign-in";
    } else {
      throw new Error("AUTH_SESSION_EXPIRED");
    }
  }

  public getInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

const apiClient = new ApiClient();
export default apiClient.getInstance();
export { ApiClient };